import { useState } from 'react';
import {
    Building,
    TrendingUp,
    AlertTriangle,
    DollarSign,
    Shield,
    ChevronLeft,
    Activity,
    Clock,
    CheckCircle,
    XCircle,
    Star,
    ArrowUpRight,
    Pause,
    Play,
    Settings,
    FileCheck,
    Lock,
    Archive,
    Snowflake,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface EmploymentWorkspaceDetailProps {
    workspaceId: string;
    onBack: () => void;
}

// Mock data for demonstration — will be replaced with real API calls
const mockEmployment = {
    workspace: {
        id: 'ws-1',
        name: 'TechCorp International',
        company: { name: 'TechCorp International', country: 'FR', vatId: 'FR12345678' },
        visibility: 'invite_only' as const,
    },
    assignment: {
        status: 'active' as const,
        autonomyLevel: 2,
        createdAt: '2026-01-15T10:00:00Z',
    },
    performance: {
        tasksCompleted: 847,
        avgResponseTime: '1.2s',
        satisfactionScore: 4.7,
        escalationRate: 3.2,
        uptimePercent: 99.8,
        weeklyTrend: +5.2,
    },
    escalations: [
        { id: 'e1', title: 'Confidential client data request', severity: 'high', status: 'pending', timestamp: '2026-02-20T08:30:00Z' },
        { id: 'e2', title: 'Budget approval above threshold', severity: 'medium', status: 'resolved', timestamp: '2026-02-19T14:00:00Z' },
        { id: 'e3', title: 'New vendor onboarding', severity: 'low', status: 'resolved', timestamp: '2026-02-18T09:15:00Z' },
    ],
    payments: {
        totalEarned: 2_840.00,
        pendingPayout: 320.00,
        lastPayment: { amount: 640.00, date: '2026-02-01T00:00:00Z', period: 'January 2026' },
        history: [
            { month: 'Jan 2026', amount: 640.00, status: 'paid' },
            { month: 'Dec 2025', amount: 580.00, status: 'paid' },
            { month: 'Nov 2025', amount: 520.00, status: 'paid' },
            { month: 'Oct 2025', amount: 560.00, status: 'paid' },
            { month: 'Sep 2025', amount: 540.00, status: 'paid' },
        ],
    },
    governance: {
        roleLockedUntil: '2026-06-15T00:00:00Z',
        scopeImmutable: true,
        memoryIsolation: true,
        rightToForget: true,
    },
    lifecycle: {
        contractStatus: 'ACTIVE' as 'ACTIVE' | 'TERMINATING' | 'FROZEN' | 'ARCHIVED',
        startDate: '2025-09-01T00:00:00Z',
        endDate: '2026-08-31T00:00:00Z',
        terminatedAt: null as string | null,
        frozenAt: null as string | null,
        archivedAt: null as string | null,
        scopeSnapshot: 'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855',
        scopeSnapshotSignature: 'HMAC-SHA256:a1b2c3d4e5f6g7h8i9j0',
        scopeDefinition: JSON.stringify({
            tasks: ['customer-support', 'data-analysis', 'report-generation'],
            autonomyLevel: 2,
            escalationThreshold: 'high',
        }),
    },
};

type Tab = 'performance' | 'escalations' | 'payments' | 'governance' | 'lifecycle';

export default function EmploymentWorkspaceDetail({ workspaceId: _workspaceId, onBack }: EmploymentWorkspaceDetailProps) {
    const { t } = useTranslation('dashboard');
    const [activeTab, setActiveTab] = useState<Tab>('performance');
    const data = mockEmployment;

    const tabs: { id: Tab; label: string; icon: typeof TrendingUp; badge?: number }[] = [
        { id: 'performance', label: t('employment.tab_performance', 'Performance'), icon: TrendingUp },
        { id: 'escalations', label: t('employment.tab_escalations', 'Escalations'), icon: AlertTriangle, badge: data.escalations.filter(e => e.status === 'pending').length },
        { id: 'payments', label: t('employment.tab_payments', 'Payments'), icon: DollarSign },
        { id: 'governance', label: t('employment.tab_governance', 'Governance'), icon: Shield },
        { id: 'lifecycle', label: t('employment.tab_lifecycle', 'Lifecycle'), icon: FileCheck },
    ];

    const severityColors: Record<string, string> = {
        high: 'text-red-400 bg-red-400/10 border-red-500/20',
        medium: 'text-amber-400 bg-amber-400/10 border-amber-500/20',
        low: 'text-green-400 bg-green-400/10 border-green-500/20',
    };

    // ═══════════════════════════════════════════════════════════════════════════
    // PERFORMANCE TAB
    // ═══════════════════════════════════════════════════════════════════════════
    const PerformanceContent = () => (
        <div className="space-y-6">
            {/* KPI Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                    { label: 'Tasks Completed', value: data.performance.tasksCompleted.toLocaleString(), icon: CheckCircle, color: 'text-emerald-400' },
                    { label: 'Avg Response Time', value: data.performance.avgResponseTime, icon: Clock, color: 'text-hig-accent' },
                    { label: 'Satisfaction', value: `${data.performance.satisfactionScore}/5`, icon: Star, color: 'text-amber-400' },
                    { label: 'Escalation Rate', value: `${data.performance.escalationRate}%`, icon: AlertTriangle, color: 'text-orange-400' },
                    { label: 'Uptime', value: `${data.performance.uptimePercent}%`, icon: Activity, color: 'text-green-400' },
                    { label: 'Weekly Trend', value: `+${data.performance.weeklyTrend}%`, icon: ArrowUpRight, color: 'text-emerald-400' },
                ].map((kpi) => (
                    <div key={kpi.label} className="card p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <kpi.icon size={16} className={kpi.color} />
                            <span className="text-xs text-hig-text-tertiary">{kpi.label}</span>
                        </div>
                        <p className="text-xl font-bold text-hig-text-primary">{kpi.value}</p>
                    </div>
                ))}
            </div>

            {/* Activity Indicator */}
            <div className="card p-6">
                <h4 className="text-sm font-semibold text-hig-text-primary mb-3">Replica Activity</h4>
                <div className="h-2 bg-hig-bg-tertiary rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full" style={{ width: '87%' }} />
                </div>
                <p className="text-xs text-hig-text-tertiary mt-2">87% of allocated capacity utilized this week</p>
            </div>
        </div>
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // ESCALATIONS TAB
    // ═══════════════════════════════════════════════════════════════════════════
    const EscalationsContent = () => (
        <div className="space-y-4">
            {data.escalations.length === 0 ? (
                <div className="card p-8 text-center">
                    <CheckCircle size={32} className="text-hig-success mx-auto mb-3" />
                    <p className="text-hig-text-secondary">No escalations — your replica is operating smoothly</p>
                </div>
            ) : (
                data.escalations.map((esc) => (
                    <div key={esc.id} className={`card p-5 border-l-4 ${esc.severity === 'high' ? 'border-l-red-500' : esc.severity === 'medium' ? 'border-l-amber-500' : 'border-l-green-500'}`}>
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                    <h4 className="text-sm font-semibold text-hig-text-primary truncate">{esc.title}</h4>
                                    <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded-full border ${severityColors[esc.severity]}`}>
                                        {esc.severity}
                                    </span>
                                </div>
                                <p className="text-xs text-hig-text-tertiary">
                                    {new Date(esc.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                {esc.status === 'pending' ? (
                                    <span className="px-3 py-1 text-xs font-semibold bg-amber-400/10 text-amber-400 rounded-full">Pending</span>
                                ) : (
                                    <span className="px-3 py-1 text-xs font-semibold bg-emerald-400/10 text-emerald-400 rounded-full">Resolved</span>
                                )}
                            </div>
                        </div>
                    </div>
                ))
            )}
        </div>
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // PAYMENTS TAB
    // ═══════════════════════════════════════════════════════════════════════════
    const PaymentsContent = () => (
        <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="card p-5">
                    <p className="text-xs text-hig-text-tertiary mb-1">Total Earned</p>
                    <p className="text-2xl font-bold text-hig-text-primary">€{data.payments.totalEarned.toLocaleString()}</p>
                </div>
                <div className="card p-5">
                    <p className="text-xs text-hig-text-tertiary mb-1">Pending Payout</p>
                    <p className="text-2xl font-bold text-amber-400">€{data.payments.pendingPayout.toLocaleString()}</p>
                </div>
                <div className="card p-5">
                    <p className="text-xs text-hig-text-tertiary mb-1">Last Payment</p>
                    <p className="text-2xl font-bold text-emerald-400">€{data.payments.lastPayment.amount.toLocaleString()}</p>
                    <p className="text-[10px] text-hig-text-tertiary mt-0.5">{data.payments.lastPayment.period}</p>
                </div>
            </div>

            {/* Payment History */}
            <div className="card p-5">
                <h4 className="text-sm font-semibold text-hig-text-primary mb-4">Payment History</h4>
                <div className="space-y-2">
                    {data.payments.history.map((payment) => (
                        <div key={payment.month} className="flex items-center justify-between py-2 border-b border-hig-divider last:border-0">
                            <span className="text-sm text-hig-text-secondary">{payment.month}</span>
                            <div className="flex items-center gap-3">
                                <span className="text-sm font-semibold text-hig-text-primary">€{payment.amount.toLocaleString()}</span>
                                <span className="px-2 py-0.5 text-[10px] font-semibold bg-emerald-400/10 text-emerald-400 rounded-full uppercase">
                                    {payment.status}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // GOVERNANCE TAB
    // ═══════════════════════════════════════════════════════════════════════════
    const GovernanceContent = () => (
        <div className="space-y-4">
            {[
                {
                    label: 'Role Lock',
                    description: `Role locked until ${new Date(data.governance.roleLockedUntil).toLocaleDateString()}`,
                    enabled: true,
                    icon: Shield,
                },
                {
                    label: 'Scope Immutability',
                    description: 'Contract scope cannot be changed unilaterally',
                    enabled: data.governance.scopeImmutable,
                    icon: Settings,
                },
                {
                    label: 'Memory Isolation',
                    description: 'Workspace memory is isolated — no cross-workspace leakage',
                    enabled: data.governance.memoryIsolation,
                    icon: Shield,
                },
                {
                    label: 'Right to Forget',
                    description: 'You can request complete data erasure at any time',
                    enabled: data.governance.rightToForget,
                    icon: XCircle,
                },
            ].map((item) => (
                <div key={item.label} className="card p-5 flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${item.enabled ? 'bg-emerald-500/10' : 'bg-hig-bg-tertiary'}`}>
                        <item.icon size={18} className={item.enabled ? 'text-emerald-400' : 'text-hig-text-tertiary'} />
                    </div>
                    <div className="flex-1">
                        <div className="flex items-center justify-between">
                            <h4 className="text-sm font-semibold text-hig-text-primary">{item.label}</h4>
                            <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded-full ${item.enabled ? 'bg-emerald-400/10 text-emerald-400' : 'bg-hig-bg-tertiary text-hig-text-tertiary'}`}>
                                {item.enabled ? 'Active' : 'Inactive'}
                            </span>
                        </div>
                        <p className="text-xs text-hig-text-secondary mt-1">{item.description}</p>
                    </div>
                </div>
            ))}
        </div>
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // LIFECYCLE TAB
    // ═══════════════════════════════════════════════════════════════════════════
    const LifecycleContent = () => {
        const lc = data.lifecycle;
        const states = [
            { id: 'ACTIVE', label: 'Active', icon: Play, reached: true },
            { id: 'TERMINATING', label: 'Terminating', icon: Clock, reached: lc.contractStatus !== 'ACTIVE' },
            { id: 'FROZEN', label: 'Frozen', icon: Snowflake, reached: ['FROZEN', 'ARCHIVED'].includes(lc.contractStatus) },
            { id: 'ARCHIVED', label: 'Archived', icon: Archive, reached: lc.contractStatus === 'ARCHIVED' },
        ];

        let parsedScope: Record<string, unknown> | null = null;
        try {
            parsedScope = JSON.parse(lc.scopeDefinition);
        } catch { /* ignore */ }

        return (
            <div className="space-y-6">
                {/* State Machine Timeline */}
                <div className="card p-6">
                    <h4 className="text-sm font-semibold text-hig-text-primary mb-5">Contract State Machine</h4>
                    <div className="flex items-center gap-0">
                        {states.map((state, i) => {
                            const Icon = state.icon;
                            const isCurrent = state.id === lc.contractStatus;
                            return (
                                <div key={state.id} className="flex items-center flex-1">
                                    <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${isCurrent
                                            ? 'border-hig-accent bg-hig-accent/10 ring-4 ring-hig-accent/20'
                                            : state.reached
                                                ? 'border-emerald-500 bg-emerald-500/10'
                                                : 'border-hig-border bg-hig-bg-tertiary'
                                            }`}>
                                            <Icon size={16} className={isCurrent ? 'text-hig-accent' : state.reached ? 'text-emerald-400' : 'text-hig-text-tertiary'} />
                                        </div>
                                        <span className={`text-[10px] font-semibold uppercase ${isCurrent ? 'text-hig-accent' : state.reached ? 'text-emerald-400' : 'text-hig-text-tertiary'}`}>
                                            {state.label}
                                        </span>
                                    </div>
                                    {i < states.length - 1 && (
                                        <div className={`flex-1 h-0.5 mx-2 rounded-full ${state.reached ? 'bg-emerald-500/40' : 'bg-hig-border'}`} />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Key Dates */}
                <div className="card p-6">
                    <h4 className="text-sm font-semibold text-hig-text-primary mb-4">Contract Dates</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {[
                            { label: 'Start Date', value: lc.startDate, color: 'text-emerald-400' },
                            { label: 'End Date', value: lc.endDate, color: 'text-hig-accent' },
                            { label: 'Terminated', value: lc.terminatedAt, color: 'text-red-400' },
                            { label: 'Frozen', value: lc.frozenAt, color: 'text-cyan-400' },
                            { label: 'Archived', value: lc.archivedAt, color: 'text-gray-400' },
                        ].filter(d => d.value).map(d => (
                            <div key={d.label} className="bg-hig-bg-tertiary rounded-lg p-3">
                                <p className="text-[10px] text-hig-text-tertiary uppercase tracking-wide mb-1">{d.label}</p>
                                <p className={`text-sm font-semibold ${d.color}`}>
                                    {new Date(d.value!).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Scope Snapshot & Signature */}
                <div className="card p-6">
                    <div className="flex items-center gap-2 mb-4">
                        <Lock size={16} className="text-hig-accent" />
                        <h4 className="text-sm font-semibold text-hig-text-primary">Scope Snapshot Verification</h4>
                    </div>
                    <div className="space-y-3">
                        <div className="bg-hig-bg-tertiary rounded-lg p-3">
                            <p className="text-[10px] text-hig-text-tertiary uppercase tracking-wide mb-1">Scope Hash (SHA-256)</p>
                            <p className="text-xs font-mono text-hig-text-secondary break-all">{lc.scopeSnapshot}</p>
                        </div>
                        <div className="bg-hig-bg-tertiary rounded-lg p-3">
                            <p className="text-[10px] text-hig-text-tertiary uppercase tracking-wide mb-1">Signature</p>
                            <p className="text-xs font-mono text-hig-text-secondary break-all">{lc.scopeSnapshotSignature}</p>
                        </div>
                        <div className="flex items-center gap-2 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                            <CheckCircle size={14} className="text-emerald-400 flex-shrink-0" />
                            <p className="text-xs text-emerald-300">Scope snapshot verified — integrity intact</p>
                        </div>
                    </div>
                </div>

                {/* Scope Definition */}
                {parsedScope && (
                    <div className="card p-6">
                        <h4 className="text-sm font-semibold text-hig-text-primary mb-4">Scope Definition</h4>
                        <pre className="bg-hig-bg-tertiary rounded-lg p-4 text-xs font-mono text-hig-text-secondary overflow-x-auto">
                            {JSON.stringify(parsedScope, null, 2)}
                        </pre>
                    </div>
                )}
            </div>
        );
    };

    // ═══════════════════════════════════════════════════════════════════════════
    // RENDER
    // ═══════════════════════════════════════════════════════════════════════════
    return (
        <div className="max-w-5xl mx-auto space-y-6 pb-12">
            {/* Header */}
            <div className="flex items-center gap-4">
                <button
                    onClick={onBack}
                    className="p-2 hover:bg-hig-bg-tertiary rounded-lg transition-colors text-hig-text-secondary"
                >
                    <ChevronLeft size={20} />
                </button>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                            <Building size={22} className="text-white" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-hig-text-primary">{data.workspace.name}</h1>
                            <div className="flex items-center gap-2 mt-0.5">
                                <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded-full ${data.assignment.status === 'active'
                                    ? 'bg-emerald-400/10 text-emerald-400'
                                    : 'bg-amber-400/10 text-amber-400'
                                    }`}>
                                    {data.assignment.status}
                                </span>
                                <span className="text-xs text-hig-text-tertiary">
                                    Autonomy Level {data.assignment.autonomyLevel}/3
                                </span>
                                <span className="text-xs text-hig-text-tertiary">·</span>
                                <span className="text-xs text-hig-text-tertiary">
                                    Since {new Date(data.assignment.createdAt).toLocaleDateString()}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button className="p-2 hover:bg-hig-bg-tertiary rounded-lg transition-colors text-hig-text-secondary" title="Pause assignment">
                        {data.assignment.status === 'active' ? <Pause size={18} /> : <Play size={18} />}
                    </button>
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="flex items-center gap-1 overflow-x-auto pb-1">
                {tabs.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.id;
                    return (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${isActive
                                ? 'bg-hig-accent/10 text-hig-accent'
                                : 'text-hig-text-secondary hover:text-hig-text-primary hover:bg-hig-bg-tertiary'
                                }`}
                        >
                            <Icon size={16} />
                            {tab.label}
                            {tab.badge !== undefined && tab.badge > 0 && (
                                <span className="w-5 h-5 flex items-center justify-center text-[10px] font-bold bg-red-500 text-white rounded-full">
                                    {tab.badge}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Tab Content */}
            {activeTab === 'performance' && <PerformanceContent />}
            {activeTab === 'escalations' && <EscalationsContent />}
            {activeTab === 'payments' && <PaymentsContent />}
            {activeTab === 'governance' && <GovernanceContent />}
            {activeTab === 'lifecycle' && <LifecycleContent />}
        </div>
    );
}
